import { Card, CardContent, CardHeader, CardTitle, DeactivatableButton } from '@folio/components';
import { QuoteTierCustomFieldRow } from '@folio/components/module-elements/QuoteTierCustomFieldRow';
import { cn } from '@folio/lib';
import { useCurrentActiveAsset } from '@folio/utils/useCurrentActiveAsset';
import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import {
  useHasPolicyProductConfig,
  usePolicyProductConfig,
} from '@folio/utils/usePolicyProductConfig';
import { SquarePenIcon } from 'lucide-react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAvailablePricing } from '../shared-quote/use-available-pricing';
import { QuoteTableChosenTier } from './QuoteTableChosenTier';
import { QuoteTableIncludedPerils } from './QuoteTableIncludedPerils';
import { QuoteTableOptionalPerils } from './QuoteTableOptionalPerils';
import { Group } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';

type QuoteTierModuleProps = {
  showModifyButton: boolean;
  showCustomFields?: boolean;
  variant?: 'broker' | 'policyholder';
};

export function QuoteTierModule({
  showModifyButton,
  showCustomFields = true,
  variant = 'broker',
}: QuoteTierModuleProps) {
  const { t } = useTranslation();
  const { policy, workingBranch } = usePolicyOnDashboard();
  const quoteTierModuleConfig = usePolicyProductConfig('quoteTierModuleConfig');
  const isGuaranteeExcessDisplayed = useHasPolicyProductConfig('isGuaranteeExcessDisplayed');

  const navigate = useNavigate();
  const { data: asset, isPending: isAssetPending } = useCurrentActiveAsset({
    policyId: policy.id,
    branchId: workingBranch?.id,
  });

  const { data: availablePricing } = useAvailablePricing(policy.id, {
    branchId: workingBranch?.id,
    // Wait for the asset: a quoted asset already carries the tier, so firing the
    // available-pricing fallback while it loads is a wasted call. Policyholders never have
    // the quoting permission on their own policy, so for them the fallback can only 403.
    enabled: !isAssetPending && !asset?.quote && variant !== 'policyholder',
  });

  const selectedTier = asset?.quote?.options.tier ?? availablePricing?.selectedTier;

  const goToTiers = useCallback(async () => {
    // workingBranch means we are in new business
    if (!workingBranch) {
      if (quoteTierModuleConfig?.goToTiersOnMta) {
        navigate(
          quoteTierModuleConfig?.goToTiersOnMta
            .replace('${policy.customer}', policy.customers[0])
            .replace('${policy.id}', policy.id),
        );
      }
    } else {
      if (quoteTierModuleConfig?.goToTiersOnNewBusiness) {
        navigate(
          quoteTierModuleConfig?.goToTiersOnNewBusiness
            .replace('${policy.customer}', policy.customers[0])
            .replace('${policy.id}', policy.id)
            .replace('${workingBranch?.id}', workingBranch?.id!),
        );
      }
    }
  }, [policy, workingBranch, navigate]);

  if (!selectedTier) {
    return null;
  }

  const excessFootnote = t(`${policy.productId}.quoteTierModule.excessFootnote`, {
    defaultValue: '',
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {t([`${policy.productId}.quoteTierModule.title`, 'quoteTierModule.title'])}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Group align="stretch" justify="end">
          <DeactivatableButton
            variant="ghost"
            onClick={goToTiers}
            icon={<SquarePenIcon className={cn('w-4 h-4 ml-2')} />}
            rules={[
              {
                isValid: policy.status !== 'POLICY_SUSPENDED' && policy.status !== 'POLICY_STOPPED',
                message: t('quoteTierModule.modifyDisabledMessage'),
              },
            ]}
            hidden={!showModifyButton}
          >
            {t('quoteTierModule.modifyButton')}
          </DeactivatableButton>
        </Group>
        <table className="w-full">
          <thead>
            {isGuaranteeExcessDisplayed && (
              <tr className="text-xs uppercase tracking-wide text-gray-500">
                <th className="hidden md:table-cell" />
                <th className="p-1 text-left font-medium">{t('quoteTierModule.perilColumn')}</th>
                <th className="p-1 text-left font-medium">{t('quoteTierModule.excessColumn')}</th>
              </tr>
            )}
            {quoteTierModuleConfig.isChosenTierDisplayed && (
              <QuoteTableChosenTier asset={asset} fallbackTier={selectedTier} />
            )}
          </thead>
          <tbody>
            {asset?.quote && (
              <>
                <QuoteTableIncludedPerils asset={asset} branchId={workingBranch?.id} />
                <QuoteTableOptionalPerils asset={asset} branchId={workingBranch?.id} />
                {showCustomFields &&
                  quoteTierModuleConfig.fieldsConfig.map(fieldConfig => (
                    <QuoteTierCustomFieldRow
                      branches={[workingBranch?.id]}
                      key={fieldConfig.path}
                      label={fieldConfig.label}
                      fieldPath={fieldConfig.path}
                      type={fieldConfig.type}
                    />
                  ))}
              </>
            )}
          </tbody>
        </table>
        {isGuaranteeExcessDisplayed && excessFootnote && (
          <Text size="xs" className="mt-3 text-gray-500">
            {excessFootnote}
          </Text>
        )}
      </CardContent>
    </Card>
  );
}
